import { useEffect, useState } from 'react';
import { api, formatMoney } from '@/lib/api';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminOrders() {
    const [orders, setOrders] = useState([]);
    const [status, setStatus] = useState('all');
    const [refundOpen, setRefundOpen] = useState(null);
    const [reason, setReason] = useState('');
    const [refunding, setRefunding] = useState(false);

    const load = async () => { const { data } = await api.get('/admin/orders', { params: status !== 'all' ? { status } : {} }); setOrders(data); };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

    const totalCommissions = orders.filter(o => ['paid','delivered','released'].includes(o.status)).reduce((s, o) => s + (o.commission_amount || 0), 0);

    const refund = async () => {
        if (!refundOpen) return;
        setRefunding(true);
        try {
            await api.post(`/admin/orders/${refundOpen.id}/refund`, { order_id: refundOpen.id, reason: reason || null });
            toast.success('Orden reembolsada');
            setRefundOpen(null); setReason('');
            load();
        } catch (e) { toast.error(e?.response?.data?.detail || 'Error al reembolsar'); }
        finally { setRefunding(false); }
    };

    return (
        <div className="space-y-4">
            <div className="flex items-baseline justify-between flex-wrap gap-3">
                <div>
                    <h1 className="text-2xl font-semibold tracking-tight">Órdenes y comisiones</h1>
                    <p className="text-sm text-muted-foreground">Total comisiones (válidas): <span className="money font-semibold">{formatMoney(totalCommissions)}</span></p>
                </div>
                <Select value={status} onValueChange={setStatus}>
                    <SelectTrigger className="w-56" data-testid="orders-status-filter"><SelectValue /></SelectTrigger>
                    <SelectContent>
                        <SelectItem value="all">Todas</SelectItem>
                        <SelectItem value="pending_payment">Pendiente pago</SelectItem>
                        <SelectItem value="paid">Pagadas</SelectItem>
                        <SelectItem value="delivered">Entregadas</SelectItem>
                        <SelectItem value="released">Liberadas</SelectItem>
                        <SelectItem value="refunded">Reembolsadas</SelectItem>
                    </SelectContent>
                </Select>
            </div>
            <div className="rounded-xl border bg-card overflow-x-auto">
                <table className="w-full text-sm">
                    <thead className="text-xs text-muted-foreground text-left border-b"><tr>
                        <th className="p-3">Fecha</th><th>Anuncio</th><th>Comprador</th><th>Vendedor</th><th>Precio</th><th>Comisión</th><th>Total</th><th>Estado</th><th></th>
                    </tr></thead>
                    <tbody data-testid="admin-orders-table">
                        {orders.map(o => (
                            <tr key={o.id} className="border-b">
                                <td className="p-3 text-xs">{new Date(o.created_at).toLocaleString('es-MX')}</td>
                                <td className="p-3 truncate max-w-[200px]">{o.ad_snapshot?.title}</td>
                                <td className="p-3">@{o.buyer?.nickname || '-'}</td>
                                <td className="p-3">@{o.seller?.nickname || '-'}</td>
                                <td className="p-3 tabular-nums">{formatMoney(o.item_price, o.currency?.toUpperCase())}</td>
                                <td className="p-3 tabular-nums">{formatMoney(o.commission_amount, o.currency?.toUpperCase())}</td>
                                <td className="p-3 tabular-nums font-semibold">{formatMoney(o.total, o.currency?.toUpperCase())}</td>
                                <td className="p-3"><Badge variant="secondary" className="capitalize">{o.status}</Badge></td>
                                <td className="p-3 text-right">
                                    {['paid', 'delivered'].includes(o.status) && (
                                        <Button size="sm" variant="outline" onClick={() => setRefundOpen(o)} data-testid={`refund-${o.id}`}><RefreshCcw className="h-3 w-3 mr-1" />Reembolsar</Button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <Dialog open={!!refundOpen} onOpenChange={() => setRefundOpen(null)}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Reembolsar orden</DialogTitle>
                        <DialogDescription>Se procesará un reembolso total en Stripe. Esta acción es irreversible.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        {refundOpen && (
                            <div className="rounded-lg bg-secondary p-3 text-sm space-y-1">
                                <div><span className="text-muted-foreground">Anuncio:</span> {refundOpen.ad_snapshot?.title}</div>
                                <div><span className="text-muted-foreground">Total:</span> {formatMoney(refundOpen.total, refundOpen.currency?.toUpperCase())}</div>
                                <div><span className="text-muted-foreground">Comprador:</span> @{refundOpen.buyer?.nickname}</div>
                            </div>
                        )}
                        <div>
                            <label className="text-xs text-muted-foreground">Motivo (opcional)</label>
                            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Explica el motivo del reembolso..." data-testid="refund-reason" />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRefundOpen(null)}>Cancelar</Button>
                        <Button onClick={refund} disabled={refunding} data-testid="refund-confirm">{refunding ? 'Procesando…' : 'Confirmar reembolso'}</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
